import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import { notifyBugsnag } from 'cfa-react-logging';
import { equals, mapObjIndexed, pick } from 'ramda';

import ooeConstants from '../constants';
import { DashboardOrder, type CustomerAccountType } from '../dashboard/schemas';
import { AvailableLocation, SecondaryContactErrors } from '../eventDetails/schemas';
import { validateDetails } from '../eventDetails/utils';
import type { Destination } from '../types/order';
import {
  addressErrorMessages,
  dateTimeErrorMessages,
  editWarningMessage,
} from '../util/customerErrorMessages';
import { stripNonPOSSpecialCharacters } from '../util/utils';
import { logDatadogError } from '../services/datadog';
import {
  AddressResult,
  DeliveryTip,
  Order,
  OrderDeliveryAddress,
  PaperGoodsOptions,
  ValidatedDeliveryAddress,
} from './schemas';
import {
  autocompleteAddress,
  getOrderDates,
  submitOrder,
  updateOrder,
  validateAddress,
  validateDeliveryRange,
} from './thunks';

export type DeliveryError = Nullable<
  | string
  | {
      key?: string;
      message?: string;
      heading?: string;
      type?: string;
    }
>;

export type State = {
  loading: {
    availableDates: boolean;
    updateOrder: boolean;
    validateAddress: boolean;
    validateDeliveryRange: boolean;
    submitOrder: boolean;
  };
  error: {
    validateAddress: DeliveryError;
    validateDeliveryRange: DeliveryError;
    updateOrder: Nullable<string>;
    availableDates: Nullable<string>;
    editOrder: Nullable<string>;
  };
  orderType?: string;
  status: string;
  destination: Destination;
  taxAmount: number;
  subTotalAmount: number;
  availableDates: string[];
  companyName: string;
  deliveryAddress: OrderDeliveryAddress;
  validatedDeliveryAddress: ValidatedDeliveryAddress;
  deliveryAddressPlaceId: Nullable<string>;
  lastAddressSentForValidation: string;
  reorder: boolean;
  transactionId?: Nullable<string>;
  availableLocations: Array<AvailableLocation>;
  deliveryTip: DeliveryTip;
  paperGoodsOptions: PaperGoodsOptions;
  id?: string;
  promiseDateTime?: string;
  totalAmount?: number;
  showWarningModal?: boolean;
  externalOrderInfo?: {
    orderId?: string;
    externalOrderSource?: string;
  };
  editOrderId: Nullable<string>;
  editOrderSavedDeliveryAddress?: OrderDeliveryAddress;
  editOrderStatus?: string;
  editOrderClient?: string;
  editOrderOriginalCustomerDetails?: {
    cfaId?: string;
    email?: string;
    phone?: string;
    givenName?: string;
    familyName?: string;
    accountType?: CustomerAccountType;
  };
  editOverrideGuestDetails?: boolean;
  editOverrideGuestDetailsModalOpen?: boolean;
  paperGoods?: boolean;
  familyName?: string;
  smsConsent: Nullable<boolean>;
  submitOrderErrorModalOpen: boolean;
  submitOrderErrorMessage: Nullable<string>;
  detailsDate: Nullable<Date>;
  detailsTime: string;
  detailsGuestCount: string;
  detailsSpecialInstructions: string;
  detailsCateringReason: string;
  detailsErrors: { time?: string; date?: string; guestCount?: string };
  secondaryContactFirstName: string;
  secondaryContactLastName: string;
  secondaryContactPhoneNumber: string;
  secondaryContactErrors: SecondaryContactErrors;
  selectedPaymentMethod: string;
};
export const initialState: State = {
  loading: {
    availableDates: false,
    updateOrder: false,
    validateAddress: false,
    validateDeliveryRange: false,
    submitOrder: false,
  },
  error: {
    validateAddress: null,
    validateDeliveryRange: null,
    updateOrder: null,
    availableDates: null,
    editOrder: null,
  },
  orderType: 'catering',
  status: 'Create',
  destination: ooeConstants.PICKUP,
  taxAmount: 0.0,
  subTotalAmount: 0.0,
  editOrderId: null,
  editOverrideGuestDetails: false,
  editOverrideGuestDetailsModalOpen: false,
  availableDates: [],
  companyName: '',
  deliveryAddress: {
    addressLine1: '',
    addressLine2: '',
    city: '',
    county: '',
    state: '',
    zip: '',
  },
  validatedDeliveryAddress: {
    streetNumber: '',
    route: '',
    subpremise: '',
    locality: '',
    administrativeAreaLevelOne: '',
    administrativeAreaLevelTwo: '',
    postalCode: '',
    country: '',
  },
  deliveryAddressPlaceId: null,
  lastAddressSentForValidation: '',
  reorder: false,
  transactionId: null,
  availableLocations: [],
  deliveryTip: {},
  paperGoodsOptions: {},
  smsConsent: null,
  submitOrderErrorModalOpen: false,
  submitOrderErrorMessage: null,
  detailsDate: null,
  detailsTime: '',
  detailsGuestCount: '',
  detailsSpecialInstructions: '',
  detailsCateringReason: '',
  detailsErrors: validateDetails({ time: '', date: null, guestCount: undefined }),
  secondaryContactFirstName: '',
  secondaryContactLastName: '',
  secondaryContactPhoneNumber: '',
  secondaryContactErrors: {},
  selectedPaymentMethod: '',
};

const slice = createSlice({
  name: 'order',
  initialState,
  reducers: {
    changeDestination: (state, action: PayloadAction<{ destination: Destination }>) => {
      state.error.validateAddress = null;
      state.error.validateDeliveryRange = null;
      state.destination = action.payload.destination;
      state.loading.updateOrder = true;
      state.smsConsent = null;
    },
    loadingDates: (state) => {
      state.availableDates = [];
      state.id = undefined;
      state.loading.availableDates = true;
      state.loading.updateOrder = true;
      state.showWarningModal = false;
    },
    resetOrderAmounts: (state) => {
      state.taxAmount = 0.0;
      state.subTotalAmount = 0.0;
      state.loading.updateOrder = false;
    },
    updatingOrder: (state) => {
      state.loading.updateOrder = true;
    },
    autocompleteAddress: (state, action: PayloadAction<{ address: string }>) => {
      if (!equals(action.payload.address, state.lastAddressSentForValidation)) {
        state.loading.validateAddress = true;
      }
    },
    updateUserAddressField: (
      state,
      action: PayloadAction<{
        field: keyof typeof state.validatedDeliveryAddress | 'companyName';
        value: string;
      }>,
    ) => {
      const { field, value } = action.payload;
      if (field === 'companyName') {
        state.companyName = value;
      } else {
        state.validatedDeliveryAddress[field] = value;
        if (field === 'subpremise') {
          state.deliveryAddress.addressLine2 = value;
        }
      }
    },
    setDeliveryAddressPlaceId: (state, action: PayloadAction<Nullable<string>>) => {
      state.deliveryAddressPlaceId = action.payload ?? null;
    },
    applyValidatedDeliveryAddress: (
      state,
      action: PayloadAction<{ autocompleteResult: AddressResult; validated: ValidatedDeliveryAddress }>,
    ) => {
      const { autocompleteResult, validated } = action.payload;

      const validatedDeliveryAddress = mapObjIndexed(
        stripNonPOSSpecialCharacters,
        pick(
          [
            'streetNumber',
            'route',
            'subpremise',
            'locality',
            'administrativeAreaLevelOne',
            'administrativeAreaLevelTwo',
            'postalCode',
            'country',
          ],
          validated as ValidatedDeliveryAddress,
        ),
      );

      const priorSubpremise = state.validatedDeliveryAddress?.subpremise ?? '';
      const subpremise = priorSubpremise.trim() ? priorSubpremise : validatedDeliveryAddress.subpremise || '';

      state.error.validateAddress = null;
      state.deliveryAddressPlaceId = autocompleteResult.placeId ?? null;
      state.validatedDeliveryAddress = validatedDeliveryAddress;
      state.validatedDeliveryAddress.subpremise = subpremise;
      state.deliveryAddress = {
        addressLine1: `${validatedDeliveryAddress?.streetNumber} ${validatedDeliveryAddress?.route}`,
        addressLine2: subpremise,
        city: validatedDeliveryAddress?.locality,
        county: validatedDeliveryAddress?.administrativeAreaLevelTwo,
        state: validatedDeliveryAddress?.administrativeAreaLevelOne,
        zip: validatedDeliveryAddress?.postalCode,
      };
    },
    dismissError: (state, action: PayloadAction<{ key: keyof State['error'] }>) => {
      const { key } = action.payload;

      if (key === 'editOrder') {
        return initialState;
      }

      if (typeof state.error[key] === 'string' && state.error[key].includes(ooeConstants.DISMISSIBLE)) {
        state.error[key] = null;
      }
    },
    setPaperGoodsOptions: (
      state,
      action: PayloadAction<{ option: keyof PaperGoodsOptions; value: boolean }>,
    ) => {
      const { option, value } = action.payload;
      state.paperGoodsOptions[option] = value;
      state.paperGoods =
        state.paperGoodsOptions.plates ||
        state.paperGoodsOptions.cutlery ||
        state.paperGoodsOptions.napkins ||
        state.paperGoodsOptions.cups;
    },
    resetPaperGoods: (state) => {
      state.paperGoodsOptions = {
        plates: false,
        cutlery: false,
        napkins: false,
        cups: false,
      };
      state.paperGoods = false;
    },
    clearAddressFields: (state) => {
      state.validatedDeliveryAddress = initialState.validatedDeliveryAddress;
      state.deliveryAddress = initialState.deliveryAddress;
      state.companyName = initialState.companyName;
      state.lastAddressSentForValidation = initialState.lastAddressSentForValidation;
      state.error.validateAddress = null;
      state.error.validateDeliveryRange = null;
    },
    invalidateValidatedDeliveryAddress: (state) => {
      const { subpremise } = state.validatedDeliveryAddress;
      state.validatedDeliveryAddress = {
        ...initialState.validatedDeliveryAddress,
        subpremise,
      };
      state.deliveryAddress = {
        ...initialState.deliveryAddress,
        addressLine2: subpremise,
      };
      state.deliveryAddressPlaceId = initialState.deliveryAddressPlaceId;
      state.lastAddressSentForValidation = initialState.lastAddressSentForValidation;
      state.error.validateAddress = null;
      state.error.validateDeliveryRange = null;
    },
    addExternalOrderId: (state, action: PayloadAction<{ orderId: string; externalOrderSource: string }>) => {
      const { orderId, externalOrderSource } = action.payload;
      state.externalOrderInfo = {
        orderId,
        externalOrderSource,
      };
    },
    initiateEditOrder: (
      state,
      action: PayloadAction<{
        order: Order | DashboardOrder;
        guest: GuestDetails | GuestSearchResult;
        reorder?: boolean;
      }>,
    ) => {
      const { order, guest, reorder } = action.payload;
      const { first, last } = guest as GuestDetails;
      const { firstName, lastName } = guest as GuestSearchResult;
      const { id, deliveryTip, paperGoods, clientId, destination, deliveryAddress, companyName, status } =
        order;

      state.error = { ...initialState.error };

      if (reorder) {
        state.reorder = true;
        state.editOrderId = null;
      } else {
        state.editOrderId = id;
        state.error.editOrder = `${editWarningMessage} ${first || firstName} ${last || lastName} ${ooeConstants.DISMISSIBLE}`;
        const dashboardOrder = order as DashboardOrder;
        state.editOrderOriginalCustomerDetails = {
          cfaId: dashboardOrder.cfaId,
          email: dashboardOrder.email,
          phone: dashboardOrder.phone,
          givenName: dashboardOrder.givenName,
          familyName: dashboardOrder.familyName,
          accountType: dashboardOrder.accountType,
        };
      }

      state.destination = destination === ooeConstants.DELIVERY ? ooeConstants.DELIVERY : ooeConstants.PICKUP;
      state.companyName = companyName ?? '';

      if (
        destination === ooeConstants.DELIVERY &&
        (typeof deliveryAddress === 'string' || !deliveryAddress)
      ) {
        notifyBugsnag({
          grouping: 'Edit order loaded with missing or string deliveryAddress',
          message: 'Initiate Edit Order - Invalid Delivery Address Format',
          error: {
            deliveryAddressType: typeof deliveryAddress,
            deliveryAddressValue: deliveryAddress,
            orderId: id,
            reorder: !!reorder,
            destination,
          },
        });
      }

      state.deliveryAddress =
        (typeof deliveryAddress === 'string' ? initialState.deliveryAddress : deliveryAddress) ??
        initialState.deliveryAddress;

      if (destination === ooeConstants.DELIVERY) {
        const { addressLine1, city, state: addressState, zip } = state.deliveryAddress;
        if (!addressLine1 || !city || !addressState || !zip) {
          notifyBugsnag({
            grouping: 'Edit order has empty delivery address fields after initialization',
            message: 'Initiate Edit Order - Empty Delivery Address Fields',
            error: {
              deliveryAddress: state.deliveryAddress,
              orderId: id,
              reorder: !!reorder,
              destination,
            },
          });
        }
      }

      state.editOrderSavedDeliveryAddress = state.deliveryAddress;
      state.editOrderStatus = status;
      state.loading.updateOrder = true;
      state.deliveryTip = reorder ? {} : (deliveryTip ?? {});
      state.paperGoods = paperGoods;
      state.paperGoodsOptions = {};
      state.editOrderClient = clientId;
    },
    setEditOrderDismissibleMessage: (state, action: PayloadAction<string>) => {
      state.error.editOrder = `${editWarningMessage} ${action.payload} ${ooeConstants.DISMISSIBLE}`;
    },
    updatePricing: () => {},
    selectPastDeliveryAddress: (_state, _action: PayloadAction<OrderDeliveryAddress>) => {},
    notReorder: (state) => {
      state.reorder = false;
    },
    closeWarningModal: (state) => {
      state.showWarningModal = false;
    },
    exitEditOrder: () => {
      return initialState;
    },
    setOverrideGuestDetails: (state, action: PayloadAction<boolean>) => {
      state.editOverrideGuestDetails = action.payload;
    },
    setOverrideGuestDetailsModalOpen: (state, action: PayloadAction<boolean>) => {
      state.editOverrideGuestDetailsModalOpen = action.payload;
    },
    setSmsConsent: (state, action: PayloadAction<{ smsConsent: Nullable<boolean> }>) => {
      state.smsConsent = action.payload.smsConsent;
    },
    setSubmitOrderErrorModalOpen: (state, action: PayloadAction<boolean>) => {
      state.submitOrderErrorModalOpen = action.payload;
    },
    setSubmitOrderErrorMessage: (state, action: PayloadAction<Nullable<string>>) => {
      state.submitOrderErrorMessage = action.payload;
    },
    setDeliveryTip: (state, action: PayloadAction<DeliveryTip>) => {
      state.deliveryTip = action.payload;
    },
    setDetailsDate: (state, action: PayloadAction<Nullable<Date>>) => {
      state.detailsDate = action.payload;
      state.detailsErrors = validateDetails({
        time: state.detailsTime,
        date: state.detailsDate,
        guestCount: state.detailsGuestCount,
      });
    },
    setDetailsTime: (state, action: PayloadAction<string>) => {
      state.detailsTime = action.payload;
      state.detailsErrors = validateDetails({
        time: state.detailsTime,
        date: state.detailsDate,
        guestCount: state.detailsGuestCount,
      });
    },
    setDetailsGuestCount: (state, action: PayloadAction<string>) => {
      state.detailsGuestCount = action.payload;
      state.detailsErrors = validateDetails({
        time: state.detailsTime,
        date: state.detailsDate,
        guestCount: state.detailsGuestCount,
      });
    },
    setDetailsSpecialInstructions: (state, action: PayloadAction<string>) => {
      state.detailsSpecialInstructions = action.payload;
    },
    setDetailsCateringReason: (state, action: PayloadAction<string>) => {
      state.detailsCateringReason = action.payload;
    },
    initializeDetailsForm: (
      state,
      action: PayloadAction<{
        date: Nullable<Date>;
        time: Nullable<string>;
        paperGoods?: boolean;
        guestCount?: Nullable<string>;
        specialInstructions?: Nullable<string>;
        cateringReason?: Nullable<string>;
      }>,
    ) => {
      state.detailsDate = action.payload.date;
      state.detailsTime = action.payload.time ?? '';
      state.detailsGuestCount = action.payload.guestCount ?? '';
      state.detailsSpecialInstructions = action.payload.specialInstructions ?? '';
      state.detailsCateringReason = action.payload.cateringReason ?? '';
      state.paperGoods = action.payload.paperGoods;
      state.detailsErrors = validateDetails({
        time: state.detailsTime,
        date: state.detailsDate,
        guestCount: state.detailsGuestCount,
      });
    },
    resetDetailsForm: (state) => {
      state.detailsDate = initialState.detailsDate;
      state.detailsTime = initialState.detailsTime;
      state.detailsGuestCount = initialState.detailsGuestCount;
      state.detailsSpecialInstructions = initialState.detailsSpecialInstructions;
      state.detailsCateringReason = initialState.detailsCateringReason;
      state.detailsErrors = initialState.detailsErrors;
    },
    setSecondaryContactFirstName: (state, action: PayloadAction<string>) => {
      state.secondaryContactFirstName = action.payload;
    },
    setSecondaryContactLastName: (state, action: PayloadAction<string>) => {
      state.secondaryContactLastName = action.payload;
    },
    setSecondaryContactPhoneNumber: (state, action: PayloadAction<string>) => {
      state.secondaryContactPhoneNumber = action.payload;
    },
    initializeSecondaryContactForm: (
      state,
      action: PayloadAction<{
        firstName?: Nullable<string>;
        lastName?: Nullable<string>;
        phoneNumber?: Nullable<string>;
      }>,
    ) => {
      state.secondaryContactFirstName = action.payload.firstName ?? '';
      state.secondaryContactLastName = action.payload.lastName ?? '';
      state.secondaryContactPhoneNumber = action.payload.phoneNumber ?? '';
    },
    resetSecondaryContactForm: (state) => {
      state.secondaryContactFirstName = initialState.secondaryContactFirstName;
      state.secondaryContactLastName = initialState.secondaryContactLastName;
      state.secondaryContactPhoneNumber = initialState.secondaryContactPhoneNumber;
      state.secondaryContactErrors = initialState.secondaryContactErrors;
    },
    setSecondaryContactErrors: (state, action: PayloadAction<SecondaryContactErrors>) => {
      state.secondaryContactErrors = action.payload;
    },
    addSecondaryContact: () => {},
    setPaymentMethod: (state, action: PayloadAction<string>) => {
      state.selectedPaymentMethod = action.payload;
    },
    initializePaymentMethod: (state, action: PayloadAction<string | undefined>) => {
      state.selectedPaymentMethod = action.payload ?? '';
    },
    resetPaymentMethod: (state) => {
      state.selectedPaymentMethod = initialState.selectedPaymentMethod;
    },
  },
  extraReducers(builder) {
    builder
      .addCase(submitOrder.pending, (state) => {
        state.loading.submitOrder = true;
      })
      .addCase(submitOrder.fulfilled, (_, action) => {
        const { order } = action.payload;
        return {
          ...initialState,
          transactionId: order.transactionId,
        };
      })
      .addCase(submitOrder.rejected, (state, action) => {
        const { error } = action;
        logDatadogError('Submit Order Error', { order: state }, error);
        state.loading.submitOrder = false;
        state.loading.availableDates = false;

        // is this structured error from rejectWithValue?
        if (action.payload && typeof action.payload === 'object' && 'errorType' in action.payload) {
          state.submitOrderErrorModalOpen = false;
          return;
        }

        state.submitOrderErrorMessage = (error?.message || '')
          .toString()
          .replace('{DESTINATION}', state.destination.toLowerCase());
        state.submitOrderErrorModalOpen = true;
      })
      .addCase(updateOrder.pending, (state) => {
        state.loading.updateOrder = true;
      })
      .addCase(updateOrder.fulfilled, (state, action) => {
        const order = action.payload;
        state.id = order.id;
        state.taxAmount = order.taxAmount ?? 0.0;
        state.subTotalAmount = order.subTotalAmount ?? 0.0;
        state.promiseDateTime = order.promiseDateTime;
        state.loading.updateOrder = false;
        state.totalAmount = order.totalAmount;
        state.deliveryTip = order.deliveryTip || {};

        if (
          typeof state.error.updateOrder !== 'string' ||
          !state.error.updateOrder.includes(ooeConstants.DISMISSIBLE)
        ) {
          state.error.updateOrder = null;
        }
      })
      .addCase(updateOrder.rejected, (state, action) => {
        const { error } = action;
        state.loading.updateOrder = false;
        state.loading.availableDates = false;
        state.error.updateOrder =
          error.message?.replace('{DESTINATION}', state.destination.toLowerCase()) ?? 'Update Order Error';
        logDatadogError('Update Order Error', { order: state }, error);
      })
      .addCase(autocompleteAddress.pending, (state) => {
        state.loading.validateAddress = true;
      })
      .addCase(autocompleteAddress.fulfilled, (state, action) => {
        const { address, results } = action.payload;
        state.lastAddressSentForValidation = address;

        if (results.length > 0) {
          return;
        }

        state.loading.validateAddress = false;
        state.error.validateAddress = addressErrorMessages.ERR_NOT_DELIVERABLE;
        state.showWarningModal = true;
        state.availableLocations = [];
      })
      .addCase(autocompleteAddress.rejected, (state, action) => {
        const { error } = action;
        logDatadogError('Autocomplete Address Error', { order: state }, error);
        state.loading.validateAddress = false;
        state.error.validateAddress = (error?.message || '')
          .toString()
          .replace('{DESTINATION}', state.destination.toLowerCase());
      })
      .addCase(getOrderDates.pending, (state) => {
        state.loading.availableDates = true;
        state.error.availableDates = null;
      })
      .addCase(getOrderDates.fulfilled, (state, action) => {
        const { dates } = action.payload;
        state.availableDates = dates;
        state.loading.availableDates = false;
        state.error.availableDates = null;
      })
      .addCase(getOrderDates.rejected, (state, action) => {
        logDatadogError('Get Order Dates Error', { order: state }, action.error);
        state.loading.availableDates = false;
        state.error.availableDates = dateTimeErrorMessages.DATES_FAILURE;
      })
      .addCase(validateDeliveryRange.pending, (state) => {
        state.loading.validateDeliveryRange = true;
        state.error.validateDeliveryRange = null;
      })
      .addCase(validateDeliveryRange.fulfilled, (state, action) => {
        const { valid, availableLocations } = action.payload;
        state.loading.validateDeliveryRange = false;
        state.availableLocations = availableLocations;

        if (!valid) {
          state.error.validateDeliveryRange = addressErrorMessages.ERR_OUT_OF_RANGE;
          state.showWarningModal = true;
        }
      })
      .addCase(validateDeliveryRange.rejected, (state, action) => {
        const { error } = action;
        logDatadogError('Validate Delivery Range Error', { order: state }, error);
        state.loading.validateDeliveryRange = false;
        state.error.validateDeliveryRange = error.message
          ? error.message.replace('{DESTINATION}', state.destination.toLowerCase())
          : addressErrorMessages.ERR_OUT_OF_RANGE;
      })
      .addCase(validateAddress.pending, (state) => {
        state.loading.validateAddress = true;
      })
      .addCase(validateAddress.fulfilled, (state, action) => {
        const { address, response } = action.payload;
        state.loading.validateAddress = false;
        state.lastAddressSentForValidation = address;

        const validatedDeliveryAddress = mapObjIndexed(
          stripNonPOSSpecialCharacters,
          pick(
            [
              'streetNumber',
              'route',
              'subpremise',
              'locality',
              'administrativeAreaLevelOne',
              'administrativeAreaLevelTwo',
              'postalCode',
              'country',
            ],
            response as ValidatedDeliveryAddress,
          ),
        );

        // A street-level result comes back with no unit. Keep the unit already on the order.
        const priorSubpremise = state.validatedDeliveryAddress?.subpremise ?? '';
        const subpremise = priorSubpremise.trim()
          ? priorSubpremise
          : validatedDeliveryAddress.subpremise || '';

        state.error.validateAddress = null;
        state.deliveryAddressPlaceId = action.meta.arg.autocompleteResult.placeId ?? null;
        state.validatedDeliveryAddress = validatedDeliveryAddress;
        state.validatedDeliveryAddress.subpremise = subpremise;
        state.deliveryAddress = {
          addressLine1: `${validatedDeliveryAddress?.streetNumber} ${validatedDeliveryAddress?.route}`,
          addressLine2: subpremise,
          city: validatedDeliveryAddress?.locality,
          county: validatedDeliveryAddress?.administrativeAreaLevelTwo,
          state: validatedDeliveryAddress?.administrativeAreaLevelOne,
          zip: validatedDeliveryAddress?.postalCode,
        };

        if (
          !validatedDeliveryAddress?.streetNumber ||
          !validatedDeliveryAddress?.route ||
          !validatedDeliveryAddress?.locality ||
          !validatedDeliveryAddress?.administrativeAreaLevelOne ||
          !validatedDeliveryAddress?.postalCode
        ) {
          notifyBugsnag({
            grouping: 'Address validation returned empty fields',
            message: 'Validate Address Fulfilled - Empty Fields',
            error: {
              validatedDeliveryAddress,
              derivedDeliveryAddress: state.deliveryAddress,
              inputAddress: address,
            },
          });
        }
      })
      .addCase(validateAddress.rejected, (state, action) => {
        const { error } = action;
        logDatadogError('Validate Address Error', { order: state }, error);
        state.loading.validateAddress = false;

        if (action.payload && typeof action.payload === 'object' && 'address' in action.payload) {
          state.error.validateAddress = addressErrorMessages.ERR_NOT_DELIVERABLE;
          state.showWarningModal = true;
          state.availableLocations = [];
          state.lastAddressSentForValidation = action.payload.address;
          return;
        }
        state.loading.availableDates = false;
        state.error.validateAddress = error;
      });
  },
});

export const actions = slice.actions;
export default slice.reducer;
